import { SortDirection, SortField } from '@/components/widget/content-sort-select';
import {
  isConversationProjectionItem,
  useContentDetail,
  useContentList,
  useNavigateWithContentFlush,
  useUser,
} from '@/hooks';
import { CANONICAL_LABELS, getContentDisplayLabel, getContentTypeDisplayLabel } from '@/lib/content-labels';
import { useSearchStore } from '@/store/searchStore';
import { useSetActiveProvenance } from '@/features/speak2me/useSetActiveProvenance';
import { useSelectedItemStore } from '@/store/selectedItemStore';
import {
  buildExploreContentListQuery,
} from '@/utils/search-utils';
import { toContentHref } from '@/utils/content-ref';
import { appendBreadcrumbStep, normalizeBreadcrumbTrail } from '@/utils/breadcrumb-state';
import { mapSortFieldToOrderBy } from '@/utils/content-sort-utils';
import { ContentConversationListItem, SearchMatch } from '@vivport/backend/types';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ListEntry } from './ExploreEntry';
import { ExploreConversationEntry } from './ExploreConversationEntry';
import { ExploreDriveResults } from './ExploreDriveResults';
import { ExploreMailboxResults } from './ExploreMailboxResults';
import { snippetQuote } from '@/utils/search-match-utils';

interface ExploreSearchResultsProps {
  cursor: number;
  limit: number;
  sortOptions: { field: SortField; direction: SortDirection };
  sortExplicit: boolean;
}

export function ExploreSearchResults({ cursor, limit, sortOptions, sortExplicit }: ExploreSearchResultsProps) {
  const { data: memberContext } = useUser();

  // Get current search query from search store
  const { query, activeSavedFilterDefinition } = useSearchStore();
  const savedDefinition = activeSavedFilterDefinition;
  const assembledContentListQuery = useMemo(() => {
    return buildExploreContentListQuery({
      query,
      tabFilters: memberContext?.tabFilters,
      savedDefinition,
      orderBy: mapSortFieldToOrderBy(sortOptions.field),
      orderDir: sortOptions.direction,
      sortExplicit,
      cursor,
      limit,
    });
  }, [
    query,
    memberContext?.tabFilters,
    savedDefinition,
    sortOptions.field,
    sortOptions.direction,
    sortExplicit,
    cursor,
    limit,
  ]);
  const activeProjection = assembledContentListQuery.state.projection;

  // Fetch data using centralized content API with parsed filters and pagination
  const contentQuery = useContentList(assembledContentListQuery.request);
  
  const { data: contentData, isLoading } = contentQuery;
  const contentIds = contentData?.ids || [];
  const activeProjectionMode = activeProjection ?? contentData?.projection ?? 'CONTENT';
  const conversationItems = useMemo(() => {
    if (activeProjectionMode !== 'CONVERSATION' || !contentData?.items) {
      return [];
    }
    return contentData.items.filter(isConversationProjectionItem);
  }, [activeProjectionMode, contentData?.items]);
  const hasResults =
    activeProjectionMode === 'CONVERSATION' ? conversationItems.length > 0 : contentIds.length > 0;
  const matches = contentData?.matches ?? {};
  const searchText = assembledContentListQuery.state.search?.trim() ?? '';

  return (
    <div data-testid="explore-search-results" className="overflow-y-auto overflow-x-hidden pb-20">
      {!isLoading && !hasResults ? (
        searchText ? (
          <div data-testid="explore-search-empty" className="w-full text-center mt-6 space-y-1 text-sm text-muted-foreground">
            <p>Nothing matched &ldquo;{searchText}&rdquo;</p>
            <p className="text-xs">Words match as words, numbers and codes match exactly, and quotes match a phrase.</p>
          </div>
        ) : (
          <div className="w-full text-center mt-6 text-sm text-muted-foreground">
            No results found.
          </div>
        )
      ) : (
        <ul className="divide-y">
          {activeProjectionMode === 'CONVERSATION'
            ? conversationItems.map((conversationItem) => (
                <ConversationListItem
                  key={conversationItem.id}
                  conversationItem={conversationItem}
                  match={matches[conversationItem.id]}
                />
              ))
            : contentIds.map((contentId) => {
                return <ContentListItem key={contentId} contentId={contentId} match={matches[contentId]} />;
              })}
        </ul>
      )}
      <ExploreMailboxResults
        mailbox={assembledContentListQuery.state.mailbox}
        searchText={searchText}
        after={assembledContentListQuery.state.after}
        before={assembledContentListQuery.state.before}
      />
      <ExploreDriveResults drives={assembledContentListQuery.state.drives} searchText={searchText} />
    </div>
  );
}

// Helper component to fetch and render Content
function ContentListItem({
  contentId,
  match,
}: {
  contentId: number;
  match?: SearchMatch;
}) {
  const { data: content } = useContentDetail(contentId);
  const { toggleItem, isItemSelected, clearSelectedItems } = useSelectedItemStore();
  const setActiveProvenance = useSetActiveProvenance();
  const navigate = useNavigateWithContentFlush();
  const location = useLocation();
  const from = normalizeBreadcrumbTrail(location.state?.from);

  if (!content) {
    return null;
  }

  const handleClick = () => {
    clearSelectedItems();
    const contentTypeName = getContentDisplayLabel(content) || CANONICAL_LABELS.documentSection;
    const destinationHref = toContentHref(content.code);
    const nextFrom = appendBreadcrumbStep(from, {
      label: contentTypeName,
      href: destinationHref,
    });
    const highlightQuote =
      content.contentType === 'MESSAGE' && match?.segmentKind === 'PASSAGE' && match.snippet
        ? snippetQuote(match.snippet)
        : '';
    navigate(destinationHref, {
      state: {
        from: nextFrom,
        ...(highlightQuote ? { highlight: { quote: highlightQuote, sourceContentId: content.id } } : {}),
      },
    });
  };

  const openNeoInSpeak2Me =
    content.contentType === 'NEO_CHAT'
      ? () => {
          setActiveProvenance({ id: content.id, type: 'NEO_CHAT', code: content.code });
        }
      : undefined;

  return (
    <ListEntry
      content={content}
      checked={isItemSelected(content.id)}
      onCheckedChange={() => toggleItem(content.id)}
      onClick={handleClick}
      match={match}
      onOpenInSpeak2Me={openNeoInSpeak2Me}
    />
  );
}

function ConversationListItem({
  conversationItem,
  match,
}: {
  conversationItem: ContentConversationListItem;
  match?: SearchMatch;
}) {
  const { toggleItem, isItemSelected, clearSelectedItems } = useSelectedItemStore();
  const { data: memberContext } = useUser();
  const navigate = useNavigateWithContentFlush();
  const location = useLocation();
  const from = normalizeBreadcrumbTrail(location.state?.from);
  const representativeMessage = conversationItem.representativeMessage;
  const selectionId = conversationItem.representativeMessageId;
  const targetRef = useMemo(() => {
    if (typeof conversationItem.rootCode === 'string' && conversationItem.rootCode.length > 0) {
      return conversationItem.rootCode;
    }
    if (representativeMessage.code) {
      return representativeMessage.code;
    }
    return String(conversationItem.representativeMessageId);
  }, [conversationItem.rootCode, conversationItem.representativeMessageId, representativeMessage.code]);

  const meEmails = useMemo(() => {
    const emails = [memberContext?.selectedMember?.email, conversationItem.externalEmail];
    return emails.filter((value): value is string => Boolean(value && value.trim().length > 0));
  }, [conversationItem.externalEmail, memberContext?.selectedMember?.email]);

  const handleClick = () => {
    clearSelectedItems();
    const contentTypeName =
      getContentTypeDisplayLabel(representativeMessage.contentType) || CANONICAL_LABELS.documentSection;
    const destinationHref = toContentHref(targetRef);
    const nextFrom = appendBreadcrumbStep(from, {
      label: contentTypeName,
      href: destinationHref,
    });
    navigate(destinationHref, {
      state: { from: nextFrom },
    });
  };

  return (
    <ExploreConversationEntry
      conversation={conversationItem}
      checked={isItemSelected(selectionId)}
      onCheckedChange={() => toggleItem(selectionId)}
      onClick={handleClick}
      match={match}
      meEmails={meEmails}
    />
  );
}


